import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sortPicksByDisplay, sortPicksByTeam, formatUserEntryDisplay } from '../utils/formatDisplay';
import { formatKickoff } from '../utils/formatKickoff';
import { useSeason } from '../contexts/SeasonContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

export function Reveal({ embedded = false, preferLatestCompleted = false, weekId = null, selectedWeek = null, fetchPicks = true, showPicksTable = true, token = '' }) {
  const { dataVersion } = useSeason();
  const [isRevealed, setIsRevealed] = useState(false);
  const [isLive, setIsLive] = useState(false); // true when client-side fallback assembles data
  const [games, setGames] = useState([]);
  const [picks, setPicks] = useState([]);
  const [pickCounts, setPickCounts] = useState({ arr: [], total: 0 });
  const [kpis, setKpis] = useState({ wins: 0, losses: 0, tbd: 0, total: 0 });
  // We don't use the `loading` value in UI; keep setter for internal state without linter warnings
  const [, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Resolve week: use provided weekId prop when present, otherwise use current week
      let resolvedWeekId = weekId ? String(weekId) : null;
      if (!resolvedWeekId) {
        const wRes = await fetch(`${API_BASE}/api/weeks/current`);
        const resolvedWeek = await wRes.json().catch(() => ({}));
        resolvedWeekId = (resolvedWeek && resolvedWeek.id) ? String(resolvedWeek.id) : null;
      }
      if (!resolvedWeekId) {
        setGames([]); setPicks([]); setIsRevealed(false); setIsLive(false); setPickCounts({ arr: [], total: 0 }); setKpis({ wins: 0, losses: 0, tbd: 0, total: 0 });
        return;
      }

      // First try: reveal-full for compatibility with tests and to use server KPIs when available
      try {
        const rfRes = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/reveal-full`);
        if (rfRes.ok) {
          const payload = await rfRes.json().catch(() => null);
          setIsRevealed(true);
          setIsLive(false); // server provided KPIs/games; not in client-side fallback
          const gamesArr = (payload && Array.isArray(payload.games)) ? payload.games : [];
          if (Array.isArray(gamesArr) && gamesArr.length) {
            setGames(gamesArr);
          } else {
            // No games included; fetch games for completeness
            try {
              const gRes2 = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/games`);
              const gJson2 = await gRes2.json().catch(() => []);
              setGames(Array.isArray(gJson2) ? gJson2 : []);
            } catch { setGames([]); }
          }
          // Prefer nested snapshot.counts (legacy reveal-full); fall back to top-level counts (Worker)
          const counts = (payload && payload.snapshot && payload.snapshot.counts)
            || (payload && payload.counts)
            || {};
          const pairs = Object.entries(counts || {});
          const total = pairs.reduce((acc, [, c]) => acc + (Number(c) || 0), 0);
          const arr = pairs.map(([team, count]) => ({ team, count: Number(count) || 0, pct: total ? Math.round(((Number(count) || 0) / total) * 1000) / 10 : 0 }));
          arr.sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));
          setPickCounts({ arr, total });
          const serverKpis = (payload && payload.kpis) || null;
          if (serverKpis && typeof serverKpis === 'object') {
            const { wins = 0, losses = 0, tbd = 0, total: kTotal = total } = serverKpis;
            setKpis({ wins, losses, tbd, total: kTotal });
          } else {
            setKpis({ wins: 0, losses: 0, tbd: 0, total });
          }
          // Picks (optional)
          if (fetchPicks) {
            try {
              const pRes = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/picks`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });
              if (!pRes.ok) {
                setPicks([]);
              } else {
                const pJson = await pRes.json().catch(() => []);
                setPicks(Array.isArray(pJson) ? pJson : []);
              }
            } catch { setPicks([]); }
          } else {
            setPicks([]);
          }
          return; // Done
        } else if (rfRes.status === 403) {
          // Not yet revealed; fetch games only and exit
          try {
            const gRes3 = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/games`);
            const gJson3 = await gRes3.json().catch(() => []);
            setGames(Array.isArray(gJson3) ? gJson3 : []);
          } catch { setGames([]); }
          setIsRevealed(false);
          setIsLive(false);
          setPicks([]);
          setPickCounts({ arr: [], total: 0 });
          setKpis({ wins: 0, losses: 0, tbd: 0, total: 0 });
          return; // Done
        }
      } catch {
        // continue to snapshot fallback
      }

      // Snapshot fallback path
      let gamesData = [];
      try {
        const gRes = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/games`);
        const gJson = await gRes.json().catch(() => []);
        gamesData = Array.isArray(gJson) ? gJson : [];
        setGames(gamesData);
      } catch { setGames([]); gamesData = []; }

    try {
        const snapRes = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/reveal-snapshot`);
        if (snapRes.ok) {
          const snapshot = await snapRes.json().catch(() => null);
          setIsRevealed(true);
      setIsLive(true); // snapshot + client KPI computation path
          const counts = snapshot && snapshot.counts ? snapshot.counts : {};
          const pairs = Object.entries(counts || {});
          const total = pairs.reduce((acc, [, c]) => acc + (Number(c) || 0), 0);
          const arr = pairs.map(([team, count]) => ({ team, count: Number(count) || 0, pct: total ? Math.round(((Number(count) || 0) / total) * 1000) / 10 : 0 }));
          arr.sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));
          setPickCounts({ arr, total });
          // Use server-provided KPIs if available, otherwise fallback to client computation
          const serverKpis = (snapshot && snapshot.kpis) || null;
          if (serverKpis && typeof serverKpis === 'object') {
            const { wins = 0, losses = 0, tbd = 0, total: kTotal = total } = serverKpis;
            setKpis({ wins, losses, tbd, total: kTotal });
          } else {
            const alive = (snapshot && typeof snapshot.alive_count === 'number') ? snapshot.alive_count : total;
            setKpis({ wins: 0, losses: 0, tbd: alive, total: alive });
          }
          if (fetchPicks) {
            try {
              const pRes = await fetch(`${API_BASE}/api/weeks/${encodeURIComponent(resolvedWeekId)}/picks`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });
              if (!pRes.ok) {
                setPicks([]);
              } else {
                const pJson = await pRes.json().catch(() => []);
                const list = Array.isArray(pJson) ? pJson : [];
                setPicks(list);
                // compute KPIs from gamesData
                let wins = 0, losses = 0, tbd = 0;
                const resultByTeam = new Map();
                for (const g of gamesData) {
                  if (!g || typeof g.home_score !== 'number' || typeof g.away_score !== 'number') continue;
                  if (g.status !== 'final') continue;
                  if (g.home_score === g.away_score) {
                    if (g.home_team_abbr) resultByTeam.set(g.home_team_abbr, 'loss');
                    if (g.away_team_abbr) resultByTeam.set(g.away_team_abbr, 'loss');
                  } else {
                    const homeWon = g.home_score > g.away_score;
                    const winner = homeWon ? g.home_team_abbr : g.away_team_abbr;
                    const loser = homeWon ? g.away_team_abbr : g.home_team_abbr;
                    if (winner) resultByTeam.set(winner, 'win');
                    if (loser) resultByTeam.set(loser, 'loss');
                  }
                }
                for (const p of list) {
                  const team = (p.team_abbr || '').trim();
                  if (!team) { tbd += 1; continue; }
                  const r = resultByTeam.get(team);
                  if (r === 'win') wins += 1; else if (r === 'loss') losses += 1; else tbd += 1;
                }
                const finalTbd = tbd + (snapshot?.no_pick_count || 0);
                setKpis({ wins, losses, tbd: finalTbd, total: (snapshot?.alive_count ?? (total || 0)) });
              }
            } catch { setPicks([]); }
          } else {
            // Keep coarse KPIs already set from snapshot (alive_count)
            setPicks([]);
          }
        } else if (snapRes.status === 403) {
          setIsRevealed(false);
          setIsLive(false);
          setPicks([]);
          setPickCounts({ arr: [], total: 0 });
          setKpis({ wins: 0, losses: 0, tbd: 0, total: 0 });
        } else {
          setIsRevealed(false);
          setIsLive(false);
          setPicks([]);
          setPickCounts({ arr: [], total: 0 });
          setKpis({ wins: 0, losses: 0, tbd: 0, total: 0 });
        }
      } catch {
        setIsRevealed(false);
        setIsLive(false);
        setPicks([]);
        setPickCounts({ arr: [], total: 0 });
        setKpis({ wins: 0, losses: 0, tbd: 0, total: 0 });
      }
    } catch (e) {
      // ignore
      setGames([]); setPicks([]); setPickCounts({ arr: [], total: 0 }); setKpis({ wins: 0, losses: 0, tbd: 0, total: 0 }); setIsRevealed(false); setIsLive(false);
    } finally {
      setLoading(false);
    }
  }, [weekId, dataVersion, fetchPicks, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gameByTeam = useMemo(() => {
    const m = new Map();
    for (const g of games || []) {
      if (!g || !g.home_team_abbr || !g.away_team_abbr) continue;
      const isFinal = g.status === 'final' && typeof g.home_score === 'number' && typeof g.away_score === 'number';
      if (isFinal) {
        // Treat ties as losses for both
        if (g.home_score === g.away_score) {
          m.set(g.home_team_abbr, 'loss');
          m.set(g.away_team_abbr, 'loss');
        } else {
          const homeWon = g.home_score > g.away_score;
          const winner = homeWon ? g.home_team_abbr : g.away_team_abbr;
          const loser = homeWon ? g.away_team_abbr : g.home_team_abbr;
          m.set(winner, 'win');
          m.set(loser, 'loss');
        }
      } else {
        if (!m.has(g.home_team_abbr)) m.set(g.home_team_abbr, 'tbd');
        if (!m.has(g.away_team_abbr)) m.set(g.away_team_abbr, 'tbd');
      }
    }
    return m;
  }, [games]);

  const countForTeam = useCallback((abbr) => {
    return (pickCounts.arr.find(x => x.team === abbr) || { count: 0 }).count || 0;
  }, [pickCounts]);

  const groupedGames = useMemo(() => {
    if (!games || games.length === 0) return [];
    const sorted = [...games].sort((a, b) => {
      const timeA = a.start_time ? new Date(a.start_time).getTime() : 0;
      const timeB = b.start_time ? new Date(b.start_time).getTime() : 0;
      return timeA - timeB;
    });

    const groups = [];
    for (const g of sorted) {
      const slotLabel = formatKickoff(g.start_time);
      let group = groups[groups.length - 1];
      if (!group || group.label !== slotLabel) {
        group = { label: slotLabel, games: [] };
        groups.push(group);
      }
      group.games.push(g);
    }
    return groups;
  }, [games]);

  const [pickSort, setPickSort] = useState('display'); // 'display' | 'team'
  const sortedPicks = useMemo(() => {
    return pickSort === 'team' ? sortPicksByTeam(picks) : sortPicksByDisplay(picks);
  }, [picks, pickSort]);

  // Silence unused-prop lint for API-compatible props not yet consumed in UI
  void embedded;
  void preferLatestCompleted;

  return (
    <div className="space-y-3 text-left">
      <div className="reveal-panel-accent text-sm font-medium text-muted-foreground">Pick Distribution & Games</div>
      {isRevealed && (
        <div className="reveal-panel rounded-md border border-border bg-card/50 p-2">
          {isLive && (
            <div className="flex justify-end">
              <span
                aria-label="Live mode (client-side fallback)"
                title="Live mode (client-side fallback)"
                className="rounded-full border border-success/50 bg-success/10 px-1.5 py-px text-[11px] uppercase tracking-wide text-success"
              >live</span>
            </div>
          )}
          <div className="reveal-kpis">
            <div className="reveal-kpi win">
              <div className="font-bold">Wins</div>
              <div className="text-xl">{kpis.wins}</div>
            </div>
            <div className="reveal-kpi loss">
              <div className="font-bold">Losses</div>
              <div className="text-xl">{kpis.losses}</div>
            </div>
            <div className="reveal-kpi tbd">
              <div className="font-bold">TBD</div>
              <div className="text-xl">{kpis.tbd}</div>
            </div>
            <div className="reveal-kpi">
              <div className="font-bold">Total</div>
              <div className="text-xl">{kpis.total}</div>
            </div>
          </div>
        </div>
      )}
      {isRevealed ? (
        <div className="reveal-panel rounded-md border border-border bg-card/50 p-3">
          <div className="mb-1.5 font-semibold">Teams</div>
          {/* 'By Team' header and Show selector removed — display is fixed to Count then Percent */}
          {pickCounts.arr.length === 0 ? (
            <div className="opacity-70">No picks</div>
          ) : (
            <div className="distribution-grid">
              {pickCounts.arr.map(x => {
                const res = gameByTeam.get(x.team) || 'tbd';
                const cls = res === 'win' ? 'win' : res === 'loss' ? 'loss' : 'tbd';
                return (
                  <React.Fragment key={x.team}>
                    <div className="distribution-row">
                      <div className={`legend-swatch ${cls}`} />
                      <div>{x.team}</div>
                    </div>
                    <div className="distribution-count">{x.count}</div>
                    <div className="distribution-pct">{`${x.pct}%`}</div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="reveal-panel rounded-md border border-border bg-card/50 p-3 text-sm text-muted-foreground">
          Distribution is hidden until lock.
        </div>
      )}

      <div className="reveal-panel mt-3 rounded-md border border-border bg-card/50 p-3">
        <div className="mb-1.5 font-semibold">Games</div>
        <table role="table" aria-label="Games and pick distribution" className="w-full border-collapse text-xs sm:text-sm">
          <thead>
            <tr>
              <th scope="col" className="border-b border-border py-1.5 px-2 text-left font-medium">Away</th>
              <th scope="col" className="border-b border-border py-1.5 px-2 text-left font-medium">Home</th>
              <th scope="col" className="border-b border-border py-1.5 px-2 text-right font-medium w-14 sm:w-16">%</th>
            </tr>
          </thead>
          <tbody>
            {groupedGames.length === 0 ? (
              <tr>
                <td colSpan={3} className="border-b border-border/60 py-1.5 px-2 text-left opacity-70">No games</td>
              </tr>
            ) : groupedGames.map((group, groupIdx) => (
              <React.Fragment key={group.label || groupIdx}>
                <tr>
                  <td
                    colSpan={3}
                    className={`border-b border-border/50 px-2 py-1 text-[11px] font-semibold text-muted-foreground tracking-wide bg-muted/20 ${
                      groupIdx > 0 ? 'pt-2.5' : 'pt-1.5'
                    }`}
                  >
                    {group.label}
                  </td>
                </tr>
                {group.games.map(g => {
                  const awayCount = countForTeam(g.away_team_abbr);
                  const homeCount = countForTeam(g.home_team_abbr);
                  const totalGamePicks = awayCount + homeCount;
                  const isFinal = g.status === 'final' && typeof g.home_score === 'number' && typeof g.away_score === 'number';
                  let awayColor, homeColor;
                  if (isFinal) {
                    if (g.home_score === g.away_score) {
                      awayColor = 'var(--loss-color)'; homeColor = 'var(--loss-color)';
                    } else if (g.home_score > g.away_score) { homeColor = 'var(--win-color)'; awayColor = 'var(--loss-color)'; }
                    else { awayColor = 'var(--win-color)'; homeColor = 'var(--loss-color)'; }
                  }
                  return (
                    <tr key={g.id}>
                      <td className="border-b border-border/60 py-1.5 px-2">
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <span
                            aria-label={`${g.away_team_abbr} picks: ${isRevealed ? awayCount : 'hidden'}`}
                            className={`inline-flex items-center justify-center min-w-[1.4rem] px-1.5 py-0.5 rounded-full text-[11px] sm:text-xs font-mono tabular-nums border transition-opacity ${
                              !isRevealed
                                ? 'opacity-40 text-muted-foreground border-transparent bg-muted/20'
                                : awayCount === 0
                                ? 'opacity-35 text-muted-foreground border-transparent bg-muted/20'
                                : 'font-semibold text-foreground border-border/60 bg-muted shadow-xs'
                            }`}
                          >
                            {isRevealed ? awayCount : '—'}
                          </span>
                          {typeof g.away_score === 'number' && (
                            <span
                              className="inline-flex items-center justify-center min-w-[1.4rem] px-1.5 py-0.5 rounded text-[11px] sm:text-xs font-mono font-semibold tabular-nums border border-border/40 bg-background/60"
                              style={{ color: awayColor }}
                            >
                              {g.away_score}
                            </span>
                          )}
                          <span className="font-medium" style={{ color: awayColor }}>
                            {g.away_team_abbr}
                          </span>
                        </div>
                      </td>
                      <td className="border-b border-border/60 py-1.5 px-2">
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <span className="font-medium" style={{ color: homeColor }}>
                            {g.home_team_abbr}
                          </span>
                          {typeof g.home_score === 'number' && (
                            <span
                              className="inline-flex items-center justify-center min-w-[1.4rem] px-1.5 py-0.5 rounded text-[11px] sm:text-xs font-mono font-semibold tabular-nums border border-border/40 bg-background/60"
                              style={{ color: homeColor }}
                            >
                              {g.home_score}
                            </span>
                          )}
                          <span
                            aria-label={`${g.home_team_abbr} picks: ${isRevealed ? homeCount : 'hidden'}`}
                            className={`inline-flex items-center justify-center min-w-[1.4rem] px-1.5 py-0.5 rounded-full text-[11px] sm:text-xs font-mono tabular-nums border transition-opacity ${
                              !isRevealed
                                ? 'opacity-40 text-muted-foreground border-transparent bg-muted/20'
                                : homeCount === 0
                                ? 'opacity-35 text-muted-foreground border-transparent bg-muted/20'
                                : 'font-semibold text-foreground border-border/60 bg-muted shadow-xs'
                            }`}
                          >
                            {isRevealed ? homeCount : '—'}
                          </span>
                        </div>
                      </td>
                      <td className="border-b border-border/60 py-1.5 px-2 text-right tabular-nums">
                        {isRevealed ? (
                          <span className={totalGamePicks === 0 ? 'opacity-40 text-muted-foreground' : 'font-medium'}>
                            {`${Math.round((totalGamePicks / (pickCounts.total || 1)) * 1000) / 10}%`}
                          </span>
                        ) : (
                          <span className="opacity-40">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="h-3" />
      {showPicksTable && (
        isRevealed ? (
          <div className="reveal-table">
            <div className="mb-1.5 flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-sm">
                Sort
                <select
                  value={pickSort}
                  onChange={e => setPickSort(e.target.value)}
                  aria-label="Sort picks"
                  className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="display">User/Entry</option>
                  <option value="team">Team</option>
                </select>
              </label>
            </div>
            <table role="table" aria-label="Weekly picks" className="relative w-full text-sm">
              <thead className="sticky">
                <tr>
                  <th scope="col" className="border-b border-border text-left font-medium">Entry</th>
                  <th scope="col" className="border-b border-border text-left font-medium">Pick</th>
                </tr>
              </thead>
              <tbody>
                {sortedPicks.map(p => {
                  // Determine final status: prefer explicit pick result, otherwise derive from games
                  const rawStatus = (p.result && String(p.result)) || gameByTeam.get(p.team_abbr) || '';
                  const status = (rawStatus || '').toLowerCase();
                  const pillClass = status === 'win' ? 'pill win' : status === 'loss' ? 'pill loss' : 'pill progress';
                  return (
                    <tr key={p.id}>
                      <td role="rowheader">
                        <span>{formatUserEntryDisplay({ user_name: p.user_name, user_email: p.user_email, entry_name: p.entry_name, entry_id: p.entry_id })}</span>
                      </td>
                      <td>
                        {p.team_abbr ? (
                          <span className={`${pillClass} inline-block rounded px-1.5 py-0.5`}>{String(p.team_abbr)}</span>
                        ) : (
                          // No pick: render nothing (blank) instead of "()"
                          null
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-2 text-sm text-muted-foreground">
            Picks will appear here after lock.
          </div>
        )
      )}

      <div className="mt-2 flex items-center gap-3 text-xs opacity-85">
        <span><span className="mr-1 inline-block h-2.5 w-2.5" style={{ background: 'var(--win-bg)', border: '1px solid var(--win-border)' }} /> Win</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5" style={{ background: 'var(--loss-bg)', border: '1px solid var(--loss-border)' }} /> Loss</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5" style={{ background: 'var(--tbd-bg)', border: '1px solid var(--tbd-border)' }} /> TBD</span>
      </div>
    </div>
  );
}

export default Reveal;
